import Accordion from '@/common/components/Accordion'
import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import { FileUploader } from '@/common/components/FileUploader'
import MaskedTextField from '@/common/components/MaskedTextField'
import Select from '@/common/components/Select'
import TextField from '@/common/components/TextField'
import useCompany from '@/common/hooks/queries/useCompany'
import { ErrorResponse } from '@/common/types/errorResponse'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { formatCurrency, formatDateRange } from '@/common/utils/formatters'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { handleServerError } from '@/common/utils/handleServerError'
import { RequestedReview } from '@/features/establishmentLeadsFunnel/components/RequestedReview'
import { GovernmentAllocationEntryReviewButtons } from '@/features/privateCompanies/components/GovernmentAllocationEntryReviewButtons'
import { governmentAllocationMovementsKind } from '@/features/privateCompanies/constants/governmentAllocations/governmentAllocationMovementsKind.constant'
import useAdvanceGovernmentAllocationEntry from '@/features/privateCompanies/hooks/useAdvanceGovernmentAllocationEntry'
import { useCompanyBranch } from '@/features/privateCompanies/hooks/useCompanyBranch'
import useRegressGovernmentAllocationEntry from '@/features/privateCompanies/hooks/useRegressGovernmentAllocationEntry'
import {
  GovernmentAllocationEntryUpdateValidationSchema,
  updateSchema,
} from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/schemas/update.schema'
import { EntryGovernmentAllocationDetails } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Update/components/EntryGovernmentAllocationDetails'
import { CompanyGovernmentAllocationEntryData } from '@/features/privateCompanies/types/companyGovernmentAllocationEntry.type'
import { Checklist } from '@/features/privateCompanyLeadsFunnel/components/Checklist'
import { zodResolver } from '@hookform/resolvers/zod'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import { CalendarDaysIcon, SearchIcon } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

export const GovernmentAllocationEntryAwaitingManagementReviewForm = ({
  companyGovernmentAllocation,
}: {
  companyGovernmentAllocation: CompanyGovernmentAllocationEntryData
}) => {
  const { id, companyId } = useParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const passedEveryRequirement = companyGovernmentAllocation?.requirement_list_summary?.steps?.every(
    (step) => step.passed,
  )

  const hasDisapprovedReview = companyGovernmentAllocation?.last_review?.state === 'disapproved'

  const [baseError, setBaseError] = useState('')

  const useFormMethods = useForm<GovernmentAllocationEntryUpdateValidationSchema>({
    resolver: zodResolver(updateSchema),
  })

  const {
    setError,
    formState: { errors },
  } = useFormMethods

  const { data: company } = useCompany({ id: companyId || '' })

  const { data: branchData } = useCompanyBranch(companyGovernmentAllocation?.government_allocation?.branch_id || '')

  const reviewCompanyGovernmentAllocationEntryIsPending = useIsMutating({
    mutationKey: ['reviewCompanyGovernmentAllocationEntry'],
  })

  const redirectOnSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocations'] })
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocationEntry', id] })
    navigate(`/companies/${companyId}/government-allocation`)
  }

  const onError = (error: ErrorResponse) => {
    handleServerError({ error, setBaseError, setError })
  }

  const { mutate: advanceGovernmentAllocationEntry, isPending: advanceGovernmentAllocationEntryIsPending } =
    useAdvanceGovernmentAllocationEntry({
      id,
      onSuccess: redirectOnSuccess,
      onError,
    })

  const { mutate: regressGovernmentAllocationEntry, isPending: regressGovernmentAllocationEntryIsPending } =
    useRegressGovernmentAllocationEntry({
      id,
      onSuccess: redirectOnSuccess,
      onError,
    })

  return (
    <Box className="flex gap-6 items-start h-[calc(100vh-130px)] shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
      <div className="w-full flex flex-col gap-6 h-[calc(100vh-178px)] overflow-y-auto">
        {baseError && <p className="text-sm text-danger-soft">{baseError}</p>}

        <div className="flex justify-between">
          <p className="text-lg font-bold">
            Detalhes{' '}
            {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'do Empenho' : 'da Cota'}
          </p>
          <GovernmentAllocationEntryReviewButtons
            governmentAllocationKind={companyGovernmentAllocation?.government_allocation?.kind}
            reviewState={companyGovernmentAllocation?.last_review?.state}
          />
        </div>

        {companyGovernmentAllocation?.last_review?.state === 'disapproved' && (
          <RequestedReview description={companyGovernmentAllocation?.last_review?.description} />
        )}

        <div className="flex gap-6">
          <div>
            <p className="text-sm font-light italic">Valor global do contrato</p>
            <p className="text-lg font-bold">{formatCurrency(Number(company?.granted_credit || 0))}</p>
          </div>

          <div>
            <p className="text-sm font-light italic">Cota de empenho da filial</p>
            <p className="text-lg font-bold">{formatCurrency(Number(branchData?.granted_credit || 0))}</p>
          </div>

          <div>
            <p className="text-sm font-light italic">
              Tipo de movimentação
            </p>

            <p className="text-lg font-bold">
              {governmentAllocationMovementsKind[companyGovernmentAllocation?.government_allocation?.movements_kind] || '--'}
            </p>
          </div>
        </div>

        {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? (
          <div className="flex gap-x-2">
            <TextField
              label="Número do ofício do Empenho"
              className="mt-5"
              name="office_number"
              defaultValue={companyGovernmentAllocation?.office_number}
              errorMessage={errors.office_number?.message}
              disabled
            />

            <div className="space-y-2">
              <p className="text-[11px] leading-3">Período de competência</p>

              <div className="flex items-start gap-x-2">
                <MaskedTextField
                  label="Início"
                  mask="00/00/0000"
                  LeftIcon={<CalendarDaysIcon strokeWidth={1} />}
                  name="government_allocation.start_of_document_validity"
                  defaultValue={
                    companyGovernmentAllocation?.government_allocation?.start_of_document_validity
                      ? dateTransformer(companyGovernmentAllocation?.government_allocation?.start_of_document_validity)
                      : undefined
                  }
                  errorMessage={errors.government_allocation?.start_of_document_validity?.message}
                  disabled
                />

                <MaskedTextField
                  label="Fim"
                  mask="00/00/0000"
                  LeftIcon={<CalendarDaysIcon strokeWidth={1} />}
                  name="government_allocation.end_of_document_validity"
                  defaultValue={
                    companyGovernmentAllocation?.government_allocation?.end_of_document_validity
                      ? dateTransformer(companyGovernmentAllocation?.government_allocation?.end_of_document_validity)
                      : undefined
                  }
                  errorMessage={errors.government_allocation?.end_of_document_validity?.message}
                  disabled
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div>
              <p className="text-sm font-light italic">N da cota estimativa</p>
              <p className="text-lg font-bold">{companyGovernmentAllocation?.office_number}</p>
            </div>

            <div>
              <p className="text-sm font-light italic">Período de competência</p>
              <p className="text-lg font-bold">
                {companyGovernmentAllocation?.government_allocation?.start_of_document_validity &&
                  companyGovernmentAllocation?.government_allocation?.end_of_document_validity &&
                  formatDateRange(
                    new Date(companyGovernmentAllocation?.government_allocation?.start_of_document_validity),
                    new Date(companyGovernmentAllocation?.government_allocation?.end_of_document_validity),
                  )}
              </p>
            </div>
          </>
        )}

        {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' && (
          <FileUploader
            name="file"
            accept="application/pdf, image/png, image/jpg, image/jpeg"
            title="Anexe o Empenho"
            fileName={
              companyGovernmentAllocation?.file?.url
                ? getFilenameFromUrl(companyGovernmentAllocation?.file?.url)
                : undefined
            }
            currentFileUrl={companyGovernmentAllocation?.file?.url ? companyGovernmentAllocation?.file?.url : undefined}
            disabled
          />
        )}

        <CurrencyTextField
          label={`Valor total ${companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'do empenho' : 'da cota'
            }`}
          markAsRequired
          maxValue={999_999_999.99}
          className="w-full"
          name="total_value"
          defaultValue={
            companyGovernmentAllocation?.total_value
              ? formatCurrency(Number(companyGovernmentAllocation?.total_value))
              : undefined
          }
          errorMessage={errors.total_value?.message}
          disabled
        />

        {companyGovernmentAllocation?.government_allocation?.movements_kind !== 'fee' && (
          <Accordion
            className="py-1"
            head={
              <p>
                Detalhamento{' '}
                {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'do Empenho' : 'da Cota'}
              </p>
            }
            body={
              <div className="space-y-4">
                <Select
                  placeholder={
                    <div className="flex items-center gap-x-2">
                      <SearchIcon />
                      <p>Pesquisar serviços</p>
                    </div>
                  }
                  value=""
                  isDisabled
                />

                {!!companyGovernmentAllocation.entry_product_categories.length && (
                  <EntryGovernmentAllocationDetails
                    companyBranchId={branchData?.id}
                    governmentAllocationId={companyGovernmentAllocation.id}
                    entryProductCategories={companyGovernmentAllocation.entry_product_categories}
                    disabled
                    hideButtons
                    controlledBy={companyGovernmentAllocation?.government_allocation?.controlled_by}
                    governmentAllocationCurrencyRestricted={company?.government_allocation_currency_restricted}
                  />
                )}
              </div>
            }
          />
        )}
      </div>

      <Checklist
        requirement_list_summary={companyGovernmentAllocation?.requirement_list_summary}
        className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
        Buttons={() => (
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              disabled={!hasDisapprovedReview}
              variant={!hasDisapprovedReview ? 'yellow' : 'yellow-black'}
              onClick={() => regressGovernmentAllocationEntry()}
            >
              Solicitar revisão
            </Button>
            <Button
              type="button"
              variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
              disabled={
                !passedEveryRequirement ||
                !!reviewCompanyGovernmentAllocationEntryIsPending ||
                advanceGovernmentAllocationEntryIsPending ||
                regressGovernmentAllocationEntryIsPending
              }
              onClick={() => advanceGovernmentAllocationEntry()}
              isLoading={advanceGovernmentAllocationEntryIsPending || regressGovernmentAllocationEntryIsPending}
            >
              Aprovar {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'Empenho' : 'Cota'}
            </Button>
          </div>
        )}
      />
    </Box>
  )
}
